import { clearDraft, draft, missingSignatures, signerEmail, signerName, startDraft } from '../data/draft'
import { rememberValues, startingValues } from '../data/prefs'
import { nextFileName, saveRelease, requestPersistentStorage, type Submission } from '../data/releases'
import { templateById } from '../template/store'
import { renderPdf } from '../pdf/render'
import { fingerprint, plainText } from '../template/document'
import { fieldsFor } from '../template/types'
import { button, confirmDialog, el, heading, toast } from '../ui/dom'
import { renderDocument } from '../ui/documentView'
import { FieldForm } from '../ui/fields'
import { SignaturePad, type SignatureExport } from '../ui/signaturePad'
import { navigate } from '../router'
import { APP_VERSION, deviceDescription } from '../version'

/**
 * The whole form on one screen: what is being filmed, the signer's details, the
 * agreement itself, the confirmation and the signature.
 *
 * One screen rather than a prepare step and a sign step. Once the production,
 * producer and governing law moved into the wording there was a single producer
 * question left, and splitting one dropdown onto its own page bought nothing but
 * a tap. Everything being visible at once also means the form can be reviewed at
 * a glance, which is how it gets checked before a shoot.
 */
export function signScreen(templateId: string): HTMLElement {
  const template = templateById(templateId)
  if (!template) {
    navigate('/')
    return el('div')
  }

  // Reuse an in-progress draft for this template (a re-render, or coming back);
  // start clean when arriving for a new signer.
  if (draft.template?.id !== template.id) {
    const initial = startingValues(template)
    // Dates start at today. Nobody should have to type a date they are standing in.
    const today = new Date().toISOString().slice(0, 10)
    for (const f of template.fields) {
      if (f.type === 'date' && !initial[f.key]) initial[f.key] = today
    }
    startDraft(template, initial)
  }

  const documentHost = el('div', { class: 'document-host' })
  const redraw = () => {
    documentHost.replaceChildren(renderDocument(template, draft.values))
  }
  redraw()

  const producerForm = new FieldForm(fieldsFor(template, 'producer'), draft.values, redraw)
  const form = new FieldForm(fieldsFor(template, 'participant'), draft.values, redraw)

  // Marked required like every other required control, not just enforced on
  // submit. A box you cannot proceed without should look like one.
  const agree = el('input', {
    type: 'checkbox',
    id: 'agree',
    class: 'checkbox',
    'aria-required': 'true',
    'aria-describedby': 'agree-error',
  })
  const agreeError = el('p', { class: 'field-error', id: 'agree-error', role: 'alert' })
  agreeError.hidden = true
  const agreeRow = el('label', { class: 'agree', for: 'agree' }, [
    agree,
    el('span', {}, [template.agreeText, el('span', { class: 'required', text: ' *', 'aria-label': ' required' })]),
  ])
  agree.addEventListener('change', () => {
    if (agree.checked) {
      agreeError.hidden = true
      agreeRow.classList.remove('invalid')
    }
  })

  const pads = new Map<string, SignaturePad>()
  const signatureSection = el('div', { class: 'stack' })
  for (const spec of template.signatures) {
    const canvas = el('canvas', { class: 'pad', 'aria-label': `${spec.label}. Draw your signature.` })
    const status = el('p', { class: 'pad-status muted', text: 'Not signed yet.' })
    const pad = new SignaturePad(canvas, (has) => {
      status.textContent = has ? 'Signed.' : 'Not signed yet.'
    })
    pads.set(spec.key, pad)

    signatureSection.append(
      el('div', { class: 'signature-block' }, [
        el('p', { class: 'field-label' }, [
          spec.label,
          spec.required ? el('span', { class: 'required', text: ' *' }) : el('span', { class: 'muted', text: ' (only if it applies)' }),
        ]),
        canvas,
        el('div', { class: 'row' }, [
          button('Clear', () => pad.clear()),
          button('Undo', () => pad.undo()),
          status,
        ]),
      ]),
    )
  }

  const finish = button('Agree and sign', () => void submit(), 'primary')

  const screen = el('div', { class: 'screen' }, [
    el('header', { class: 'app-bar' }, [
      button('Back', () => {
        void (async () => {
          const ok = await confirmDialog(
            'Discard this form?',
            'Nothing has been saved yet. The details and signature will be lost.',
            'Discard',
          )
          if (ok) {
            clearDraft()
            navigate('/')
          }
        })()
      }, 'text'),
      el('h1', { class: 'app-title', text: template.title }),
    ]),
    heading('Before you hand over the phone'),
    producerForm.render(),
    heading('Your details'),
    form.render(),
    heading('The agreement'),
    documentHost,
    agreeRow,
    agreeError,
    heading('Signature'),
    signatureSection,
    el('div', { class: 'sticky-actions' }, [finish]),
  ])

  // Canvases have no intrinsic size until they are in the document.
  requestAnimationFrame(() => {
    for (const pad of pads.values()) pad.resize()
    noteIfRead()
  })
  window.addEventListener('resize', () => {
    for (const pad of pads.values()) pad.resize()
  })

  function noteIfRead(): void {
    if (draft.readToEnd) return
    // The document is part of the page now, so this is simply whether its
    // bottom has come into view. On a form short enough to fit, that is true
    // immediately, which is correct: it was all visible.
    if (documentHost.getBoundingClientRect().bottom <= window.innerHeight + 24) {
      draft.readToEnd = true
    }
  }
  window.addEventListener('scroll', noteIfRead, { passive: true })

  async function submit(): Promise<void> {
    if (finish.disabled) return

    const missingProducer = producerForm.validate()
    const missingParticipant = form.validate()
    const missingFields = [...missingProducer, ...missingParticipant]
    if (missingFields.length) {
      // Focus the first gap in reading order, not the last form checked.
      if (missingProducer.length) producerForm.focusFirstMissing()
      else form.focusFirstMissing()
      toast(`Still needed: ${missingFields.join(', ')}`)
      return
    }
    if (!agree.checked) {
      // The box sits above the signature, so from the submit button it is off
      // screen. A toast alone leaves someone hunting for what to fix.
      agreeError.textContent = 'Required: confirm you have read and agree.'
      agreeError.hidden = false
      agreeRow.classList.add('invalid')
      agreeRow.scrollIntoView({ block: 'center', behavior: 'smooth' })
      agree.focus({ preventScroll: true })
      toast('Tick the box to confirm you agree.')
      return
    }

    // Export the pads before checking for missing signatures, so a pad that has
    // ink in it counts even though nothing has been "submitted" yet.
    for (const [key, pad] of pads) {
      const exported = await pad.export()
      if (exported) draft.signatures[key] = exported
      else delete draft.signatures[key]
    }
    const missing = missingSignatures()
    if (missing.length) {
      toast(`Still needed: ${missing.join(', ')}`)
      return
    }

    finish.disabled = true
    finish.textContent = 'Creating the PDF…'
    try {
      const submission = await generate(template!)
      // Carry forward whatever this template says to, before the draft goes.
      rememberValues(template!, draft.values)
      clearDraft()
      navigate(`/done/${encodeURIComponent(submission.id)}`)
    } catch (e) {
      toast(`Could not create the PDF: ${(e as Error).message}`)
      finish.disabled = false
      finish.textContent = 'Agree and sign'
    }
  }

  return screen
}

async function generate(template: NonNullable<typeof draft.template>): Promise<Submission> {
  const values = { ...draft.values }
  const ordered = template.signatures
    .map((spec) => ({ key: spec.key, signature: draft.signatures[spec.key] }))
    .filter((entry): entry is { key: string; signature: SignatureExport } => Boolean(entry.signature))

  const documentText = plainText(template, values)
  const hash = await fingerprint(
    documentText,
    ordered.map((entry) => entry.signature.bytes),
  )

  const now = new Date()
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const name = signerName()

  const result = await renderPdf(
    template,
    values,
    ordered.map((entry) => ({ key: entry.key, bytes: entry.signature.bytes })),
    {
      signerName: name,
      signedAtDisplay: now.toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'medium' }),
      timeZone,
      device: deviceDescription(),
      appVersion: APP_VERSION,
      templateId: template.id,
      templateVersion: template.version,
      fingerprint: hash,
      readToEnd: draft.readToEnd,
      agreed: template.agreeText,
    },
  )

  if (result.substituted) {
    toast('Some characters could not be written to the PDF exactly. Check the finished document.')
  }

  const submission: Submission = {
    id: crypto.randomUUID(),
    templateId: template.id,
    templateTitle: template.title,
    templateVersion: template.version,
    signerName: name,
    signerEmail: signerEmail(),
    values,
    signedAtMillis: now.getTime(),
    signedAtIso: now.toISOString(),
    timeZone,
    device: deviceDescription(),
    appVersion: APP_VERSION,
    readToEnd: draft.readToEnd,
    fingerprint: hash,
    pdfFileName: await nextFileName(name, now),
    pageCount: result.pageCount,
    pdf: new Blob([result.bytes as BlobPart], { type: 'application/pdf' }),
  }

  await saveRelease(submission)
  void requestPersistentStorage()
  return submission
}
